<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount } from 'vue';
  import { ICreateOptionDto } from '@/api/services/options.service';
  import V2ModalOption from '@/components/modals/option/V2ModalOption.vue';
  import { modalOptionSettings } from '@/components/modals/option/modal-option-settings';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import V2CatalogGroupPickerDialog from '@/components/v2/catalog/dialogs/V2CatalogGroupPickerDialog.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { useModal } from '@/composition/use-modal';
  import { groupTemplateDraft } from '@/domain/catalog/group-templates';
  import {
    draftFromGroup,
    optionUsage,
    TGroupPickerResult,
  } from '@/domain/catalog/option-groups';
  import {
    IOptionLinkRow,
    linkRowFromDraft,
    linkRowFromOption,
  } from '@/domain/catalog/product-form';
  import { IOption } from '@/interfaces/IOption';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';
  import V2CatalogOptionGroupRow from './V2CatalogOptionGroupRow.vue';

  /**
   * «Opciones» (plan §5.13): the product's groups (shared with other
   * products), their ⋯, and «+ Agregar grupo de opciones». Group edits
   * commit on their own (the group is shared); links and new groups are
   * saved with the product's «Guardar».
   */
  const editor = useProductEditorContext();
  const { data } = useCatalogPage();
  const modals = useModal();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  /** Distinct modal names: two dialogs must not hide each other. */
  let dialogSequence = 0;

  const rows = computed(() => editor.form.value?.optionLinks ?? []);
  const readOnly = computed(() => editor.readOnly.value);
  const usage = computed(() => optionUsage(data.items.value));
  const linkedIds = computed(() =>
    rows.value.flatMap((row) => (row.optionId !== null ? [row.optionId] : []))
  );

  function setRows(next: IOptionLinkRow[]) {
    editor.update('optionLinks', next);
  }

  function updateRow(key: string, patch: Partial<IOptionLinkRow>) {
    setRows(
      rows.value.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  }

  /**
   * A draft's new content; a row that became a saved group meanwhile stays
   * as it is (a row is a link or a draft, never both).
   */
  function updateDraft(key: string, draft: ICreateOptionDto) {
    setRows(
      rows.value.map((row) =>
        row.key === key && row.draft ? { ...row, draft } : row
      )
    );
  }

  function removeRow(key: string) {
    setRows(rows.value.filter((row) => row.key !== key));
  }

  function moveRow(key: string, delta: -1 | 1) {
    const index = rows.value.findIndex((row) => row.key === key);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= rows.value.length) return;
    const next = [...rows.value];
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
  }

  //#region Group editor (V2ModalOption)

  /** A new or copied group, as a draft created with the product's «Guardar». */
  function editDraft(
    draft: ICreateOptionDto,
    onSaved: (saved: ICreateOptionDto) => void,
    creating = false
  ) {
    modals.show(
      V2ModalOption,
      { deferred: true, draftData: draft, creating },
      modalOptionSettings(),
      {
        draftSaved: (saved: ICreateOptionDto) => {
          if (!unmounted) onSaved(saved);
        },
      }
    );
  }

  /** «Editar grupo»: a shared group commits on its own. */
  function editGroup(row: IOptionLinkRow) {
    if (row.draft) {
      editDraft(row.draft, (saved) => updateDraft(row.key, saved));
      return;
    }
    if (!row.option) return;
    const optionId = row.optionId;
    modals.show(
      V2ModalOption,
      { optionToEdit: row.option, duplicateAsDraft: true },
      modalOptionSettings(),
      {
        saved: (option: IOption) => {
          if (unmounted) return;
          editor.noteGroupSaved();
          updateRow(row.key, { option });
          // The realtime echo may come late: the products using it now.
          if (optionId !== null)
            void data.refreshItems(usage.value[optionId] ?? []);
        },
        // «Duplicar» inside the editor: an independent copy replaces the link.
        draftSaved: (draft: ICreateOptionDto) => {
          if (unmounted) return;
          setRows(
            rows.value.map((candidate) =>
              candidate.key === row.key ? linkRowFromDraft(draft) : candidate
            )
          );
        },
      }
    );
  }

  /** «Usar una copia solo aquí»: the group with the rule this product applies. */
  function copyHere(row: IOptionLinkRow) {
    if (!row.option) return;
    editDraft(
      draftFromGroup(row.option, row.overrides),
      (draft) =>
        setRows(
          rows.value.map((candidate) =>
            candidate.key === row.key ? linkRowFromDraft(draft) : candidate
          )
        ),
      true
    );
  }

  //#endregion

  //#region Add

  async function addGroup() {
    const restaurantId = data.restaurant.value?.id;
    const form = editor.form.value;
    if (!restaurantId || !form) return;
    const category = data.categoryById.value.get(form.categoryId);
    const menuId = category?.menuId ?? null;
    dialogSequence += 1;
    const dialogName = `catalog-groups-${dialogSequence}`;
    const result = await modals.showDynamicDialog<TGroupPickerResult | null>(
      V2CatalogGroupPickerDialog,
      {
        dialogName,
        restaurantId,
        branchId: data.branchId.value ?? 0,
        menuId,
        linkedIds: linkedIds.value,
        products: data.items.value.filter(
          (item) =>
            item.id !== editor.itemId &&
            (data.categoryById.value.get(item.categoryId)?.menuId ?? null) ===
              menuId
        ),
        usage: usage.value,
      },
      dialogName,
      {
        adaptive: true,
        width: 480,
        maxWidth: window.innerWidth * 0.95,
        height: 'auto',
        scrollable: true,
        clickToClose: true,
      }
    );
    if (!result || unmounted) return;
    if (result.kind === 'template')
      editDraft(
        groupTemplateDraft(result.template),
        (draft) => setRows([...rows.value, linkRowFromDraft(draft)]),
        true
      );
    else if (result.kind === 'use')
      setRows([...rows.value, linkRowFromOption(result.option)]);
    else if (result.kind === 'copy')
      editDraft(
        draftFromGroup(result.option),
        (draft) => setRows([...rows.value, linkRowFromDraft(draft)]),
        true
      );
    else
      setRows([
        ...rows.value,
        ...result.links.map((link) =>
          linkRowFromOption(link.option, link.active)
        ),
      ]);
  }

  //#endregion
</script>

<template>
  <V2CatalogEditorSection
    class="v2-catalog-option-groups-section"
    :title="t('menuPage.groups.title')"
    flush
  >
    <p v-if="!rows.length" class="groups-empty">
      {{ t('menuPage.groups.empty') }}
    </p>
    <V2CatalogOptionGroupRow
      v-for="(row, index) in rows"
      :key="row.key"
      :row="row"
      :used-in="row.optionId !== null ? (usage[row.optionId] || []).length : 0"
      :first="index === 0"
      :last="index === rows.length - 1"
      :read-only="readOnly"
      :saving="editor.saving.value"
      @edit="editGroup(row)"
      @toggle="updateRow(row.key, { active: !row.active })"
      @move="moveRow(row.key, $event)"
      @copy="copyHere(row)"
      @remove="removeRow(row.key)"
    />
    <div v-if="!readOnly" class="groups-add">
      <PosUiButton
        color="neutral"
        variant="ghost"
        icon="plus"
        @click="addGroup"
        >{{ t('menuPage.groups.add') }}</PosUiButton
      >
    </div>
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-option-groups-section {
    .groups-empty {
      @apply tw-m-0 tw-px-4 tw-pb-2 tw-text-sm tw-text-neutral-500;
    }

    .groups-add {
      @apply tw-px-2 tw-pb-2 tw-pt-1;
    }
  }
</style>
